import React, {
  useMemo,
  useState,
  useEffect,
  useCallback,
  useRef,
} from "react";
import { Grid2, Radio, RadioGroup, FormControlLabel, Box } from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import HourglassFullOutlinedIcon from "@mui/icons-material/HourglassFullOutlined";
import CancelIcon from "@mui/icons-material/Cancel";
import TeamStyle from "./TeamMembersStyle.module.scss";
import DataTable from "../../../../../../GlobalComponents/DataTable/DataTable";
import { useMakeApiCalls } from "../../../../../../Hooks/useMakeApiCalls";
import { Actions } from "../../../../../../redux/constants/actionTypes";
import { getStore } from "../../../../../../Utils/storeUtils";
import { useSelector } from "react-redux";
import {
  roleMapping,
  statusMapping,
  dropdownOptionsSuperAdmin,
  dropdownOptionsAdmin,
} from "../../../../../../Utils/TeamMembersContants";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import FormPopup from "../../../../../../GlobalComponents/FormPopup/FormPopup";
import { getRoleInOrg } from "../../../../../../Utils/organizations";
import InviteMember from "../InviteMember/InviteMember";
import PageLoading from "../../../../../../GlobalComponents/PageLoading/PageLoading";
import BulkInvite from "../BulkInvite/BulkInvite";
import NewPopup from "../../../../../../GlobalComponents/Popup/NewPopup";
import { lastSegmentPath } from "../../../../../../Utils/roconConstants";
import { useEmailId } from "../../../../../../Utils/utils";

function TeamMembers() {
  const { orgId } = useParams();
  const navigate = useNavigate();
  const [teamMembers, setTeamMembers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState("All");
  const [inviteMember, setInviteMember] = useState(false);
  const [bulkInvite, setBulkInvite] = useState(false);
  const [emailVals, setEmailValues] = useState([]); // Initial value is an array with one email field
  const [role, setRole] = useState("");
  const [rowLoading, setRowLoading] = useState({});
  const [dropdownLoading, setIsDropdownLoading] = useState({});
  const [createOrgFormBulk, setCreateOrgFormBulk] = useState([]);
  const [bulkData, setBulkData] = useState([]);
  const [bulkFile, setBulkFile] = useState(null);
  const [bulkFileName, setBulkFileName] = useState("");
  const [bulkFileSize, setBulkFileSize] = useState("");
  const [redundantEmails, setRedundantEmails] = useState([]);
  const [isInviteMemberSelected, setIsInviteMemberSelected] = useState(true);
  const [isInviteDisabled, setIsInviteDisabled] = useState(false);
  const [isBulkDisabled, setIsBulkDisabled] = useState(false);
  const [errors, setErrors] = useState([]);
  const [popup, setPopup] = useState({
    popupState: false,
    popupType: "",
    popupTitle: "",
    popupSubtitle: "",
    popupSubtitle1: "",
    popupButton: false,
    popupCancel: false,
    popupButtonTitle: "",
    popupButtonFunction: () => {},
    popupCancelTitle: "",
    popupCancelFunction: () => {},
  });

  const location = useLocation();
  const lastSegment = lastSegmentPath(location);

  // // Save data to localStorage when unmounting
  // useEffect(() => {
  //   return () => {
  //     // Save the bulk invite data in localStorage
  //     localStorage.setItem(
  //       "createOrgFormBulk",
  //       JSON.stringify(createOrgFormBulk)
  //     );
  //   };
  // }, [createOrgFormBulk]);

  // // Load the data from localStorage on mount
  // useEffect(() => {
  //   const savedData = localStorage.getItem("createOrgFormBulk");
  //   if (savedData) {
  //     setCreateOrgFormBulk(JSON.parse(savedData));
  //   }
  // }, []);

  const selectedOrg = useSelector(
    (state) => state.organizationsInfo.selectedOrg
  );

  const emailId = useEmailId();
  const userDetails = useSelector((state) => state.users?.userDetails);

  const indvPlan = useSelector(
    (state) => state.plansInfo?.specificPlan?.specificPlans
  );
  const orgHasPlan = indvPlan?.some((plan) => plan.basePlan === true);

  const members = React.useMemo(
    () => selectedOrg?.members || [],
    [selectedOrg]
  );

  useMakeApiCalls(() => {
    getStore().dispatch({
      type: Actions.SET_ORG_DETAILS,
      data: {
        orgId: orgId,
        orgPage: lastSegment,
      },
    });
    getStore().dispatch({
      type: Actions.API_GET_SEPCIFIC_PLAN,
      data: { orgId: orgId },
    });
  });

  useEffect(() => {
    const isValidEmail = (email) =>
      /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(email);

    const emailId = members.map((e) => e.emailId);
    const isEmailIdInBulk = createOrgFormBulk.some(
      (entry) => entry.memberEmailId === emailId
    );
    const emailVerif = (vals) => {
      const emailSet = new Set();
      // Check for duplicates and valid emails
      const hasDuplicates = vals.some((mail) => {
        if (emailId.includes(mail) || emailSet.has(mail)) {
          return true;
        }
        emailSet.add(mail);
        return false;
      });

      return (
        (!hasDuplicates &&
          vals.every((mail, index) => isValidEmail(mail) && role[index])) ||
        isEmailIdInBulk
      );
    };

    const isInviteInvalid = !emailVerif(emailVals);
    setIsInviteDisabled(isInviteInvalid);

    const isBulkInvalid =
      createOrgFormBulk?.length === 0 ||
      (createOrgFormBulk?.length > 0 &&
        createOrgFormBulk.some(
          (entry) =>
            !entry.memberEmailId ||
            !isValidEmail(entry.memberEmailId) ||
            emailId.includes(entry.memberEmailId) ||
            !entry.roleId ||
            ![3, 4, 5].includes(entry.roleId) ||
            isEmailIdInBulk
        ));

    // Only update if there's a change in the bulk disabled state
    if (isBulkDisabled !== isBulkInvalid) {
      setIsBulkDisabled(isBulkInvalid);
    }
  }, [
    emailVals,
    role,
    createOrgFormBulk,
    members,
    isInviteDisabled,
    isBulkDisabled,
  ]);

  //popup
  const ActivatePopup = (type, message, onConfirm) => {
    if (type === "success") {
      setPopup({
        popupState: true,
        popupType: "success",
        popupTitle: "Member Removed",
        popupSubtitle:
          "The member has been successfully removed from the organization.",
        popupCancel: false,
        popupButton: true,
        popupButtonTitle: "Ok",
        popupButtonFunction: () => {
          setPopup({ popupState: false });
        },
      });
    } else if (type === "error") {
      setPopup({
        popupState: true,
        popupType: "error",
        popupTitle: message || "Something went wrong",
        popupSubtitle: "An error occurred. Please try again.",
        popupCancel: false,
        popupButton: true,
        popupButtonTitle: "Ok",
        popupButtonFunction: () => {
          setPopup({ popupState: false });
        },
      });
    } else if (type === "warning") {
      setPopup({
        popupState: true,
        popupType: "warning",
        popupTitle: message || "Something went wrong",
        popupCancel: true,
        popupButton: true,
        popupButtonTitle: "Yes",
        popupCancelTitle: "No",
        popupCancelFunction: () => {
          setPopup({ popupState: false });
        },
        popupButtonFunction: () => {
          setPopup({ popupState: false });
          if (onConfirm) onConfirm();
        },
      });
    } else if (type === "success_invite") {
      setPopup({
        popupState: true,
        popupType: "success",
        popupTitle: "Members Invited",
        popupSubtitle: "Invitations have been sent to the selected members.",
        popupCancel: false,
        popupButton: true,
        popupButtonTitle: "Ok",
        popupButtonFunction: () => {
          setPopup({ popupState: false });
        },
      });
    } else if (type === "warning_role") {
      setPopup({
        popupState: true,
        popupType: "warning",
        popupTitle: message || "Something went wrong",
        popupButton: true,
        popupButtonTitle: "Ok",
        popupButtonFunction: () => {
          setPopup({ popupState: false });
        },
      });
    } else if (type === "none") {
      setPopup({ popupState: false });
    }
  };

  useMakeApiCalls(() => {
    getStore().dispatch({ type: Actions.API_ORG_DASHBOARD });
  });

  // get selected org info
  useEffect(() => {
    if (orgId) {
      getStore().dispatch({
        type: Actions.API_GET_SELECTED_ORG_FULL_INFO,
        orgId,
      });
    }
  }, [orgId]);

  const newRoleId = members.roleId;

  const filteredRows = useMemo(() => {
    if (filter === "All") return teamMembers;
    return teamMembers.filter((row) => row.status === filter);
  }, [filter, teamMembers]);

  const rowsWithLoading = filteredRows.map((row, index) => ({
    ...row,
    loading: rowLoading[index] || false,
    dropdownLoad: dropdownLoading[index] || false,
  }));

  const columns = [
    { id: "name", label: "Name" },
    { id: "email", label: "Email" },
    {
      id: "role",
      label: "Role",
      tooltip: "Click to view your role privileges",
      tooltipOnClick: () => {
        window.open(
          "https://docs.roconpaas.io/home/user-role-priviliges/",
          "_blank"
        );
      },
      hasDropdown: true,
      dropdownOptions:
        getRoleInOrg(emailId, selectedOrg) === "Admin"
          ? dropdownOptionsAdmin
          : dropdownOptionsSuperAdmin,
    },
    { id: "status", label: "Status" },
  ];

  const updateTeamMembers = useCallback(() => {
    if (members && members?.length > 0) {
      const ownerMember = members.find((member) => member.roleDesc === "Owner");
      const otherMembers = members.filter(
        (member) => member.roleDesc !== "Owner"
      );

      const sortedMembers = ownerMember
        ? [ownerMember, ...otherMembers]
        : members;

      setTeamMembers(
        sortedMembers.map((member) => ({
          name: member.name ? (
            `${member.name}${member.emailId === emailId ? " (You)" : ""}`
          ) : (
            <b>User is not registered</b>
          ),
          email: member.emailId,
          role: roleMapping[member.roleDesc] || "",
          status: statusMapping[member.status] || "",
        }))
      );
    }
  }, [members, emailId]);

  useEffect(() => {
    updateTeamMembers();
  }, [updateTeamMembers, selectedOrg]);

  const statusCounts = useMemo(() => {
    return {
      All: teamMembers?.length,
      Active: teamMembers.filter((row) => row.status === "Active")?.length,
      Pending: teamMembers.filter((row) => row.status === "Pending")?.length,
      Rejected: teamMembers.filter((row) => row.status === "Rejected")?.length,
    };
  }, [teamMembers]);

  const statusConfig = {
    active: {
      label: "Active",
      icon: <CheckCircleIcon />,
      color: "#2AC078",
    },
    pending: {
      label: "Pending",
      icon: <HourglassFullOutlinedIcon />,
      color: "#ffa500",
    },
    rejected: {
      label: "Rejected",
      icon: <CancelIcon />,
      color: "#ff0000",
    },
  };

  const handleTitle = (status) => {
    setFilter(status);
  };

  const handleRoleChange = useCallback(
    (onDropdownSelect, rowIndex, page, rowsPerPage) => {
      const actualIndex = page * rowsPerPage + rowIndex;
      const filteredIndex = teamMembers.findIndex(
        (member) => member.email === filteredRows[actualIndex].email
      );
      const memberToUpdate = members[filteredIndex];
      const selectedRole = dropdownOptionsSuperAdmin.find(
        (option) => option.value === onDropdownSelect
      );
      const newRoleId = selectedRole?.roleId;

      if (memberToUpdate.roleId === newRoleId) {
        ActivatePopup(
          "warning_role",
          "The user already has this role. Please select a different role."
        );
        return;
      }

      setIsDropdownLoading((prev) => ({ ...prev, [rowIndex]: true }));
      getStore().dispatch({
        type: Actions.UPDATE_MEMBER_ROLE,
        orgInfo: selectedOrg,
        member: memberToUpdate,
        newRole: newRoleId,
        meta: {
          onSuccess: () => {
            setIsDropdownLoading((prev) => ({ ...prev, [rowIndex]: false }));
            getStore().dispatch({
              type: Actions.API_GET_SELECTED_ORG_FULL_INFO,
            });
          },
          onError: () => {
            setIsDropdownLoading((prev) => ({ ...prev, [rowIndex]: false }));
          },
        },
      });
    },
    [members, selectedOrg, filteredRows, teamMembers]
  );

  useEffect(() => {
    if (newRoleId !== members.roleId) {
      handleRoleChange(newRoleId);
    }
  }, [newRoleId, members.roleId, handleRoleChange]);

  function deleteMember(rowIndex, page, rowsPerPage) {
    const actualIndex = page * rowsPerPage + rowIndex;
    const filteredIndex = teamMembers.findIndex(
      (member) => member.email === filteredRows[actualIndex].email
    );
    const memberToDelete = members[filteredIndex];

    ActivatePopup(
      "warning",
      `Are you sure you want to remove ${memberToDelete.emailId}?`,
      () => {
        setRowLoading((prev) => ({ ...prev, [rowIndex]: true }));
        getStore().dispatch({
          type: Actions.DELETE_MEMBER_FROM_ORG,
          orgInfo: selectedOrg,
          member: memberToDelete,
          memberIndex: filteredIndex,
          meta: {
            onSuccess: () => {
              setRowLoading((prev) => ({ ...prev, [rowIndex]: false }));
              if (memberToDelete.emailId === emailId) {
                navigate("/account/buy-plan");
              } else {
                getStore().dispatch({
                  type: Actions.API_GET_SELECTED_ORG_FULL_INFO,
                });
                ActivatePopup("success");
              }
            },
            onError: (errorMessage) => {
              ActivatePopup("error", errorMessage);
              setRowLoading((prev) => ({ ...prev, [rowIndex]: false }));
            },
          },
        });
      }
    );
  }

  const handleCloseInvite = () => {
    setInviteMember(false);
  };

  const flushInvite = () => {
    setInviteMember(false);
    setEmailValues([""]);
    setRole("");
    setCreateOrgFormBulk([]);
    setBulkData([]);
    setBulkFile(null);
    setBulkFileName("");
    setBulkFileSize("");
  };

  const prevBulkRef = useRef(createOrgFormBulk?.length);

  // to clear bulk data when members are removed manually from list
  useEffect(() => {
    if (prevBulkRef.current > 0 && createOrgFormBulk?.length === 0) {
      setEmailValues([""]);
      setRole("");
      setCreateOrgFormBulk([]);
      setBulkData([]);
      setBulkFile(null);
      setBulkFileName("");
      setBulkFileSize("");
    }
    prevBulkRef.current = createOrgFormBulk?.length;
  }, [createOrgFormBulk]);

  let domain = window.location.hostname;
  domain =
    domain === "localhost" ? process.env.REACT_APP_METADATA_DOMAIN : domain;

  const handleInviteUser = () => {
    if (emailVals?.length > 0) {
      const users = emailVals.map((email, index) => {
        return { emailId: email, roleId: role[index] };
      });

      setLoading(true);
      getStore().dispatch({
        type: Actions.API_INVITE_USERS,
        invitedUsers: users,
        org: selectedOrg,
        domain: domain,
        meta: {
          onSuccess: () => {
            setLoading(false);
            handleCloseInvite();
            flushInvite();
            ActivatePopup("success_invite");
          },
          onError: () => {
            setLoading(false);
            handleCloseInvite();
            flushInvite();
            ActivatePopup("error");
          },
        },
      });
    }
  };

  function handleInviteClick() {
    if (createOrgFormBulk?.length > 0) {
      setLoading(true);
      getStore().dispatch({
        type: Actions.API_BULK_INVITE_USERS,
        invitedUsers: createOrgFormBulk,
        org: selectedOrg,
        domain: domain,
        meta: {
          onSuccess: () => {
            setLoading(false);
            handleCloseInvite();
            flushInvite();
            ActivatePopup("success_invite");
          },
          onError: () => {
            setLoading(false);
            handleCloseInvite();
            flushInvite();
            ActivatePopup("error");
          },
        },
      });
    } else {
      setInviteMember(false);
      ActivatePopup("error", "Please Upload A File");
    }
  }

  const handleRemoveAllClick = () => {
    setBulkFile(null);
    setBulkData([]);
    setBulkFileName("");
    setBulkFileSize("");
    setErrors([]);
    setRedundantEmails([]);
    setCreateOrgFormBulk([]);
  };

  return (
    <>
      {selectedOrg ? (
        <Grid2 className={TeamStyle.Container}>
          <Grid2 className={TeamStyle.header}>Team</Grid2>
          <DataTable
            columns={columns}
            rows={rowsWithLoading}
            enableSelection={false}
            enablePagination={true}
            enableTeamDelete={true}
            loading={loading}
            statusConfig={statusConfig}
            isStatusColumn={(row) => row.id === "status"}
            memberRole={getRoleInOrg(emailId, selectedOrg)}
            onDropdownSelect={(value, index, page, rowsPerPage) =>
              handleRoleChange(value, index, page, rowsPerPage)
            }
            dropdownHeader={TeamStyle.dropdownHeader}
            dropdownList={TeamStyle.dropdownList}
            onClick={deleteMember}
            headerProps={{
              titleOptions: [
                {
                  label: "All",
                  count: statusCounts.All,
                  color: "#00bfa5",
                  onClick: () => handleTitle("All"),
                },
                {
                  label: "Active",
                  count: statusCounts.Active,
                  color: "#00c853",
                  onClick: () => handleTitle("Active"),
                },
                {
                  label: "Pending",
                  count: statusCounts.Pending,
                  color: "#fbc02d",
                  onClick: () => handleTitle("Pending"),
                },
                {
                  label: "Rejected",
                  count: statusCounts.Rejected,
                  color: "#d32f2f",
                  onClick: () => handleTitle("Rejected"),
                },
              ].filter((option) => option.count > 0),
              ...(getRoleInOrg(emailId, selectedOrg) === "Admin" ||
              getRoleInOrg(emailId, selectedOrg) === "Super Admin" ||
              getRoleInOrg(emailId, selectedOrg) === "Owner"
                ? {
                    button: "Add Team Members",
                    
                  }
                : {}),
             buttonFun: () => {
                if (!orgHasPlan) {
                  setPopup({
                    popupState: true,
                    popupType: "warning",
                    popupTitle: "Active Plan Required",
                    popupSubtitle:
                      "Your organization needs an active plan before you can add team members. Please activate a plan to continue.",
                    popupCancel: false,
                    popupButton: true,
                    popupButtonTitle: "Ok",
                    popupButtonFunction: () => {
                      setPopup({ popupState: false });
                    },
                  });
                  return;
                }
                setInviteMember(true);
                setEmailValues([""]);
                setRole("");
              },
              header: true,
              activeFilter: filter,
            }}
          />
        </Grid2>
      ) : (
        <PageLoading />
      )}

      <NewPopup
        trigger={inviteMember || bulkInvite}
        closePopup={() => {
          setInviteMember(false);
          setBulkInvite(false);
          flushInvite();
          setCreateOrgFormBulk([]);
        }}
        header={"Add Team Member"}
        customWidth={"45%"}
        backPopup={isInviteMemberSelected ? "Cancel" : ""}
        backOnClick={() => {
          flushInvite();
        }}
        nextpopup={"Invite"}
        nextOnClick={() => {
          isInviteMemberSelected ? handleInviteUser() : handleInviteClick();
        }}
        loading={loading}
        disabled={isInviteMemberSelected ? isInviteDisabled : isBulkDisabled}
      >
        {/* Radio buttons to switch between InviteMember and BulkInvite */}
        <RadioGroup
          value={isInviteMemberSelected ? "inviteMember" : "bulkInvite"}
          onChange={(e) => {
            setIsInviteMemberSelected(e.target.value === "inviteMember");
            setEmailValues([""]);
            setRole("");
            setCreateOrgFormBulk([]);
            setBulkData([]);
            setBulkFile(null);
            setBulkFileName("");
            setBulkFileSize("");
          }}
          className={TeamStyle.options}
        >
          <Box display="flex" alignItems="center">
            <FormControlLabel
              value="inviteMember"
              control={<Radio />}
              label="Add Member"
              sx={{
                "& .MuiFormControlLabel-label": {
                  fontSize: "14px",
                  fontWeight: "500",
                  color: userDetails?.theme === "dark" ? "#ffffff" : "#000000",
                },
                "& .MuiRadio-root": {
                  color:
                    inviteMember === "inviteMember"
                      ? "#135fc2 !important"
                      : userDetails?.theme === "dark"
                      ? "#ffffff"
                      : "#000000",
                },
              }}
            />
            <FormControlLabel
              value="bulkInvite"
              control={<Radio />}
              label="Bulk Invite"
              sx={{
                "& .MuiFormControlLabel-label": {
                  fontSize: "14px",
                  fontWeight: "500",
                  color: userDetails?.theme === "dark" ? "#ffffff" : "#000000",
                },
                "& .MuiRadio-root": {
                  color:
                    bulkInvite === "bulkInvite"
                      ? "#135fc2 !important"
                      : userDetails?.theme === "dark"
                      ? "#ffffff"
                      : "#000000",
                },
              }}
            />
          </Box>
        </RadioGroup>
        <Grid2>
          {/* Conditionally render the InviteMember or BulkInvite component */}
          {isInviteMemberSelected ? (
            <InviteMember
              emailVals={emailVals}
              role={role}
              roleOptions={
                getRoleInOrg(emailId, selectedOrg) === "Admin"
                  ? dropdownOptionsAdmin
                  : dropdownOptionsSuperAdmin
              }
              setEmailValues={setEmailValues}
              setRole={setRole}
              onInviteClick={handleInviteUser}
              loading={loading}
              members={members}
            />
          ) : (
            <BulkInvite
              createOrgFormBulk={createOrgFormBulk}
              setCreateOrgFormBulk={setCreateOrgFormBulk}
              orgInfo={selectedOrg}
              onCancel={() => {
                setBulkInvite(false);
                flushInvite();
              }}
              onInvite={handleInviteClick}
              bulkData={bulkData}
              setBulkData={setBulkData}
              bulkFile={bulkFile}
              setBulkFile={setBulkFile}
              bulkFileName={bulkFileName}
              setBulkFileName={setBulkFileName}
              bulkFileSize={bulkFileSize}
              setBulkFileSize={setBulkFileSize}
              members={members}
              redundantEmails={redundantEmails}
              setRedundantEmails={setRedundantEmails}
              errors={errors}
              setErrors={setErrors}
              handleRemoveAllClick={handleRemoveAllClick}
            />
          )}
        </Grid2>
      </NewPopup>
      <section>
        <FormPopup
          trigger={popup.popupState}
          setTrigger={setPopup}
          type={popup.popupType}
          title={popup.popupTitle}
          subtitle={popup.popupSubtitle}
          button={popup.popupButton}
          cancel={popup.popupCancel}
          buttonTitle={popup.popupButtonTitle}
          buttonFunction={popup.popupButtonFunction}
          cancelTitle={popup.popupCancelTitle}
          cancelFunction={popup.popupCancelFunction}
        />
      </section>
    </>
  );
}

export default TeamMembers;
